import React, { useCallback, useMemo, useState } from 'react'

import { ApolloCache } from '@apollo/client'
import { LoadingButton } from '@mui/lab'
import { Stack } from '@mui/material'
import { GridRowId, GridSortItem } from '@mui/x-data-grid'
import { Decimal } from 'decimal.js'
import { toast } from 'react-toastify'

import { FplDataGrid } from 'components/common'
import ConfirmDialog from 'components/common/Dialog/ConfirmDialog'
import {
  BookingCharge,
  BookingStatus,
  DriverBid,
  DriverBidFilterInput,
  useAllocateDriverBidsMutation,
  useDeallocateDriverBidsMutation,
  useDisableDriverBidMutation,
  useGetBookingQuery,
  useGetExchangeRatesQuery,
} from 'generated/graphql'
import { GET_BOOKING_BIDS_QUERY_PAGED } from 'graphql/queries/DriverBidQueries'
import { getVisibleCharges } from 'helpers/bookingCharges.helpers'
import useGetDriverBidsColumns from 'hooks/fplDataGridColumns/useGetDriverBidsColumns'

interface IProps {
  bookingId: string | null
}

type AllocatedBid = {
  id: number
  driverId: number
  isAllocated: boolean
}

const AvailableBids = (props: IProps) => {
  const { bookingId } = props

  const [allocatedBidsIds, setAllocatedBidsIds] = useState<AllocatedBid[]>([])
  const [confirmOpen, setConfirmOpen] = useState<boolean>(false)
  const [openUnblockModal, setOpenUnblockModal] = useState(false)
  const [blockedDriverName, setBlockedDriverName] = useState<string>('')
  const [openDisableModal, setOpenDisableModal] = useState(false)
  const [selectedDisableBidId, setSelectedDisableBidId] = useState<number | null>(null)

  const isListOfBidsEmpty = allocatedBidsIds.length === 0

  // Queries
  const { data: bookingData } = useGetBookingQuery({
    variables: { bookingId: Number(bookingId) },
  })

  const { data: latestExchangeRates } = useGetExchangeRatesQuery({
    variables: { where: { targetCurrencyId: { eq: 1 } }, last: 3 },
    nextFetchPolicy: 'cache-and-network',
  })

  const isBookingReadOnly = useMemo(() => {
    const status = bookingData?.booking?.status
    return (
      !!status &&
      [
        BookingStatus.PackageOnBoardOnWay,
        BookingStatus.PackageOnBoardArrival,
        BookingStatus.PackageOnBoardPartial,
        BookingStatus.PackageOnBoard,
        BookingStatus.ProofOfDeliveryOnWay,
        BookingStatus.ProofOfDeliveryArrival,
        BookingStatus.ProofOfDeliveryPartial,
        BookingStatus.ProofOfDelivery,
        BookingStatus.Complete,
        BookingStatus.Invoice,
      ].includes(status)
    )
  }, [bookingData?.booking?.status])

  const revenue: number = React.useMemo(() => {
    if (bookingData?.booking) {
      const consignmentFee = bookingData.booking.consignmentFee
      const chargesList = getVisibleCharges(bookingData.booking.charges as BookingCharge[])

      const otherCharges =
        chargesList?.reduce(
          (previousValue, currentValue) =>
            currentValue?.rate * currentValue.quantity + previousValue,
          0,
        ) || 0

      return new Decimal(consignmentFee).plus(otherCharges).toNumber()
    } else {
      return 0
    }
  }, [bookingData])

  // Mutations
  const [allocateBids, { loading: allocateBidsLoading }] = useAllocateDriverBidsMutation({
    onCompleted: (data) => {
      if (data.allocateDriverBids?.length > 0) {
        toast.success(`Drivers were allocated with success.`)
      } else {
        toast.error(`Something went wrong while allocation. Please try again later.`)
      }
    },
    update: (cache) => {
      cache.evict({
        id: cache.identify({ __typename: 'Booking', id: Number(bookingId) }),
        fieldName: 'hasWinningBidsAwaitingAllocation',
      })
    },
  })

  const [deallocateBids, { loading: deallocateBidsLoading }] = useDeallocateDriverBidsMutation({
    onCompleted: (data) => {
      if (data.deallocateDriverBids?.length > 0) {
        toast.success(`Drivers were deallocated with success.`)
      } else {
        toast.error(`Something went wrong while deallocation. Please try again later.`)
      }
    },
    update: (cache) => {
      cache.evict({
        id: cache.identify({ __typename: 'Booking', id: Number(bookingId) }),
        fieldName: 'hasWinningBidsAwaitingAllocation',
      })
    },
  })

  const modifyBid = (cache: ApolloCache<any>, driverBidId: number) => {
    cache.modify({
      id: cache.identify({ __typename: 'DriverBid', id: driverBidId }),
      fields: {
        isActive() {
          return false
        },
      },
    })
  }

  const [disableDriverBid] = useDisableDriverBidMutation({
    onCompleted: (data) => {
      if (data.disableDriverBid) {
        toast.success('Bid dismissed successfully.')
      } else {
        console.error('Failed to dismiss bid.')
      }
    },
  })

  const handleDisableBid = (driverBidId: number) => {
    setSelectedDisableBidId(driverBidId)
    setOpenDisableModal(true)
  }

  const confirmDisableBid = () => {
    if (selectedDisableBidId !== null) {
      disableDriverBid({
        variables: { driverBidId: selectedDisableBidId },
        update: (cache) => {
          modifyBid(cache, selectedDisableBidId)
        },
      })
      setOpenDisableModal(false)
      setSelectedDisableBidId(null)
    }
  }

  const handleApprove = async () => {
    if (bookingId === null && allocatedBidsIds.length === 0) {
      return
    }

    const bidsToAllocate: AllocatedBid[] = []

    const bidsToRemove: AllocatedBid[] = []

    allocatedBidsIds.forEach((bid) => {
      if (bid.isAllocated) {
        bidsToRemove.push(bid)
      } else {
        bidsToAllocate.push(bid)
      }
    })

    const allocate = async () => {
      if (bidsToAllocate.length > 0) {
        await allocateBids({
          variables: {
            ids: bidsToAllocate.map((bid) => bid.id),
          },
        })
      }
    }

    const deallocate = async () => {
      if (bidsToRemove.length > 0) {
        await deallocateBids({
          variables: {
            bookingId: Number(bookingId),
            allocatedDriverIds: bidsToRemove.map((bid) => bid.driverId),
          },
        })
      }
    }

    await Promise.all([allocate(), deallocate()])

    setAllocatedBidsIds([])
  }

  const handleUnblockAndAllocate = () => {
    setOpenUnblockModal(false)
    handleApprove()
  }

  const handleOnSaveClick = () => {
    setConfirmOpen(true)
  }

  const isRowSelectable = useCallback(
    (id: GridRowId) => {
      return !allocatedBidsIds.some((bid) => bid.id === Number(id))
    },
    [allocatedBidsIds],
  )

  const handleAllocateClick = useCallback(
    (bid: DriverBid) => () => {
      setAllocatedBidsIds((prev) =>
        prev.some((b) => b.id === Number(bid.id))
          ? prev.filter((b) => b.id !== Number(bid.id))
          : [
              ...prev,
              { id: Number(bid.id), driverId: Number(bid.driverId), isAllocated: bid.isAllocated },
            ],
      )

      if (bid.isAllocated === false) {
        if (!bid.driver.isActive) {
          setBlockedDriverName(bid.driver.name)
          setOpenUnblockModal(true)
          return
        }
      }
    },
    [],
  )

  const handleOnCancelClick = () => {
    setAllocatedBidsIds([])
    setOpenUnblockModal(false)
  }

  const getGrossProfit = useCallback(
    ({ totalPrice, currencyId }: { totalPrice: number; currencyId: string }) => {
      const rate: number | undefined = latestExchangeRates?.exchangeRates?.edges?.find(
        (exchangeRate) => exchangeRate.node.sourceCurrencyId === Number(currencyId),
      )?.node.rate

      return new Decimal(revenue).minus(totalPrice * (rate ?? 1)).toFixed(2)
    },
    [latestExchangeRates?.exchangeRates?.edges, revenue],
  )

  const getGrossProfitPct = useCallback(
    ({ totalPrice, currencyId }: { totalPrice: number; currencyId: string }) => {
      if (revenue === 0) {
        return 'n/a'
      }
      const rate: number | undefined = latestExchangeRates?.exchangeRates?.edges?.find(
        (exchangeRate) => exchangeRate.node.sourceCurrencyId === Number(currencyId),
      )?.node.rate

      return new Decimal(revenue)
        .minus(totalPrice * (rate ?? 1))
        .mul(100)
        .div(revenue)
        .toFixed(2)
        .toString()
    },
    [latestExchangeRates?.exchangeRates?.edges, revenue],
  )

  const columns = useGetDriverBidsColumns(
    allocateBidsLoading,
    deallocateBidsLoading,
    handleAllocateClick,
    isRowSelectable,
    getGrossProfit,
    getGrossProfitPct,
    handleDisableBid,
    isBookingReadOnly,
  )

  const rowSelectableDefault = () => {
    return false
  }

  const filter: DriverBidFilterInput = useMemo(() => {
    return {
      bookingId: { eq: Number(bookingId) },
      isEnabled: { eq: true },
    }
  }, [bookingId])

  const queryData = useMemo(
    () => ({
      query: GET_BOOKING_BIDS_QUERY_PAGED,
      entityName: 'bookingBidsPaged',
      queryVariables: { bookingId: Number(bookingId) },
      defaultSortOrder: {
        field: 'name',
        sort: 'asc',
      } as GridSortItem,
      variableSortOrder: {
        field: 'name',
        sort: 'asc',
      } as GridSortItem,
      pageOptions: { pageSize: 5, rowsPerPage: [5, 10] },
    }),
    [bookingId],
  )

  return (
    <>
      <FplDataGrid
        query={queryData.query}
        entityName={queryData.entityName}
        overwriteId={queryData.entityName}
        queryVariables={queryData.queryVariables}
        columns={columns}
        skipQuery={!bookingId}
        defaultOrder={queryData.defaultSortOrder}
        variableSortOrder={queryData.variableSortOrder}
        pageOptions={queryData.pageOptions}
        filter={filter}
        isHideOldData={true}
        isRowSelectable={rowSelectableDefault}
        fetchPolicy='network-only'
      />

      <Stack padding={1} flexDirection='row' justifyContent='flex-end'>
        <LoadingButton
          color='primary'
          type='submit'
          loading={deallocateBidsLoading || allocateBidsLoading}
          disabled={isListOfBidsEmpty || deallocateBidsLoading || allocateBidsLoading || isBookingReadOnly}
          onClick={handleOnSaveClick}>
          Approve
        </LoadingButton>
      </Stack>

      <ConfirmDialog
        open={confirmOpen}
        onConfirm={allocatedBidsIds.length > 0 ? handleApprove : null}
        setOpen={setConfirmOpen}
        title={`Approve Bids Allocation`}
        message={`You chose to approve the selected Bids?`}
        option1='Cancel'
        option2='Approve'
      />

      <ConfirmDialog
        open={openUnblockModal}
        onConfirm={handleUnblockAndAllocate}
        message={`The Driver ${blockedDriverName} you selected is blocked due to not respecting the protocols.`}
        setOpen={setOpenUnblockModal}
        onCancel={handleOnCancelClick}
        title={`Driver Account Blocked`}
        option1='Cancel'
        option2='Unblock and allocate'
        tooltip1='Do not allocate the job to this driver.'
        tooltip2='Unblock the driver to accepting new jobs and allocate for the selected.'
      />

      <ConfirmDialog
        open={openDisableModal}
        onConfirm={confirmDisableBid}
        setOpen={setOpenDisableModal}
        title='Dismiss Bid'
        message='You chose to dismiss the selected bid?'
        option1='Cancel'
        option2='Proceed'
      />
    </>
  )
}

export default AvailableBids
